import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import styled from 'styled-components';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — react-icons + React 19 typings
import {
  FaBook as FaBookRaw,
  FaCalendarAlt as FaCalendarAltRaw,
  FaChevronDown as FaChevronDownRaw,
  FaChevronRight as FaChevronRightRaw,
  FaClock as FaClockRaw,
  FaComments as FaCommentsRaw,
  FaPaperclip as FaPaperclipRaw,
  FaPlus as FaPlusRaw,
  FaUserFriends as FaUserFriendsRaw,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import {
  teacherService,
  type TeacherActivitySummary,
  type TeacherClassSummary,
  type TeacherGradeRow,
  type TeacherLesson,
} from '../../services/classroom/teacherService';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaBook: any = FaBookRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaCalendarAlt: any = FaCalendarAltRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronDown: any = FaChevronDownRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaChevronRight: any = FaChevronRightRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaClock: any = FaClockRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaComments: any = FaCommentsRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPaperclip: any = FaPaperclipRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaPlus: any = FaPlusRaw;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FaUserFriends: any = FaUserFriendsRaw;

const MOBILE_BREAKPOINT = '960px';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 340px);
  gap: 1.5rem;
  align-items: start;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    grid-template-columns: 1fr;
  }
`;

const MainCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
`;

const SideCol = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
`;

const Hero = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.5rem 1.75rem;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
`;

const HeroText = styled.div`
  min-width: 0;
  flex: 1;
`;

const WelcomeTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: var(--text);
  letter-spacing: -0.02em;
`;

const SummaryLine = styled.p`
  margin: 0.15rem 0 0;
  font-size: 0.95rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.5;
  max-width: 42rem;
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 1.15rem;
  font-size: 0.9rem;
  font-weight: 600;
  font-family: 'Open Sans', sans-serif;
  color: var(--onPrimary);
  background: var(--primary);
  border-radius: 10px;
  text-decoration: none;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: background 0.2s ease, box-shadow 0.2s ease;
  &:hover {
    background: var(--primaryDark);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  }
`;

const Section = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 16px;
  padding: 1.25rem 1.35rem 1.35rem;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.1rem;
`;

const SectionTitle = styled.h3`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text);
`;

const SectionTitleIcon = styled.span`
  display: inline-flex;
  color: var(--primary);
  opacity: 0.9;
`;

const TextLink = styled(Link)`
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--primary);
  text-decoration: none;
  white-space: nowrap;
  &:hover {
    text-decoration: underline;
  }
`;

const CardLink = styled(Link)`
  color: inherit;
  text-decoration: none;
`;

const ClassCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1rem;
`;

const ClassCard = styled.article`
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid var(--outline);
  background: var(--surface);
  display: flex;
  flex-direction: column;
  min-height: 220px;
`;

const ClassCardVisual = styled.div<{ $variant: 'a' | 'b'; $imageUrl?: string | null }>`
  position: relative;
  height: 112px;
  background: ${({ $variant, $imageUrl }) => {
    const gradient =
      $variant === 'b'
        ? 'linear-gradient(135deg, var(--primaryDark) 0%, var(--primary) 100%)'
        : 'linear-gradient(135deg, var(--primary) 0%, var(--primaryDark) 100%)';
    const url = typeof $imageUrl === 'string' ? $imageUrl.trim() : '';
    if (url) {
      return `linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.2) 45%, transparent 100%), url(${url}) center / cover no-repeat, ${gradient}`;
    }
    return gradient;
  }};
`;

const SubjectPill = styled.span<{ $hue: 'a' | 'b' | 'c' }>`
  position: absolute;
  z-index: 1;
  left: 0.65rem;
  bottom: 0.55rem;
  padding: 0.2rem 0.55rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  background: ${({ $hue }) => {
    if ($hue === 'a') return 'var(--primaryContainer, var(--surfaceVariant))';
    if ($hue === 'b') return 'var(--surface)';
    return 'var(--tertiaryContainer, var(--surfaceVariant))';
  }};
  color: var(--primary);
  border: 1px solid var(--outline);
`;

const ClassCardBody = styled.div`
  padding: 0.85rem 1rem 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
`;

const ClassTitle = styled.h4`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text);
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  font-size: 0.8rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
`;

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: var(--surfaceVariant);
  overflow: hidden;
  margin-top: auto;
`;

const ProgressFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: ${({ $color }) => $color};
`;

const LessonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

const LessonRow = styled.div`
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 0.85rem;
  align-items: stretch;
  @media (max-width: 520px) {
    grid-template-columns: 64px minmax(0, 1fr);
  }
`;

const LessonDateBlock = styled.div`
  border-radius: 12px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.35rem;
  text-align: center;
`;

const LessonDateLabel = styled.span`
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  color: var(--primary);
`;

const LessonDateDay = styled.span`
  font-size: 1.35rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  color: var(--text);
  line-height: 1.1;
`;

const LessonDateMonth = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  text-transform: uppercase;
`;

const LessonCard = styled.div`
  border-radius: 12px;
  border: 1px solid var(--outline);
  padding: 0.85rem 1rem;
  background: var(--surface);
`;

const LessonTop = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.35rem 0.75rem;
  margin-bottom: 0.35rem;
`;

const LessonTopRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const LessonName = styled.span`
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
  font-size: 0.95rem;
  color: var(--text);
`;

const LessonClassTag = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
`;

const LessonToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 0.24rem 0.5rem;
  cursor: pointer;
  font-family: inherit;
  &:hover {
    color: var(--primary);
    border-color: var(--primary);
  }
`;

const LessonDesc = styled.p`
  margin: 0 0 0.55rem;
  font-size: 0.85rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  line-height: 1.45;
`;

const PillRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const Pill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.72rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: var(--surfaceVariant);
  color: var(--textSecondary, var(--onSurfaceVariant));
  border: 1px solid var(--outline);
`;

const ActivitySection = styled(Section)`
  padding-bottom: 1rem;
`;

const ActivityMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.8rem;
`;

const ActivityMetricCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surfaceVariant);
  padding: 0.6rem 0.65rem;
`;

const ActivityMetricLabel = styled.div`
  font-size: 0.72rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

const ActivityMetricValue = styled.div`
  margin-top: 0.15rem;
  font-size: 1rem;
  font-weight: 700;
  color: var(--text);
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ActivityItem = styled.div`
  display: grid;
  grid-template-columns: 40px minmax(0, 1fr);
  gap: 0.65rem;
  padding: 0.65rem 0;
  position: relative;
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 48px;
    bottom: -4px;
    width: 2px;
    background: var(--outline);
    border-radius: 1px;
  }
`;

const Avatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 999px;
  background: var(--primaryContainer, var(--surfaceVariant));
  color: var(--primary);
  font-size: 0.75rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid var(--surface);
  z-index: 1;
`;

const ActivityBody = styled.div`
  min-width: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text);
`;

const ActivityMeta = styled.div`
  font-size: 0.75rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
  margin-top: 0.25rem;
`;

const GradeLink = styled(Link)`
  display: inline-block;
  margin-top: 0.35rem;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--primary);
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ScoreBadge = styled.div`
  margin-top: 0.35rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  color: var(--primary);
`;

const ActivityFooter = styled(Link)`
  display: block;
  margin-top: 0.75rem;
  padding: 0.65rem;
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--textSecondary, var(--onSurfaceVariant));
  background: var(--surfaceVariant);
  border-radius: 10px;
  border: 1px solid var(--outline);
  text-decoration: none;
  &:hover {
    color: var(--primary);
    border-color: var(--primary);
  }
`;

const InsightsCard = styled.div`
  border-radius: 16px;
  padding: 1.15rem 1.25rem 1.25rem;
  background: linear-gradient(155deg, var(--primaryDark) 0%, var(--primary) 100%);
  color: var(--onPrimary);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
`;

const InsightsTitle = styled.h3`
  margin: 0 0 1rem;
  font-family: 'Poppins', sans-serif;
  font-size: 1rem;
  font-weight: 600;
`;

const InsightRow = styled.div`
  margin-bottom: 1rem;
  &:last-of-type {
    margin-bottom: 0.75rem;
  }
`;

const InsightLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  margin-bottom: 0.35rem;
  opacity: 0.95;
`;

const InsightBarTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.25);
  overflow: hidden;
`;

const InsightBarFill = styled.div<{ $pct: number; $tint: 'blue' | 'warm' }>`
  height: 100%;
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  border-radius: 999px;
  background: ${({ $tint }) => ($tint === 'warm' ? 'var(--warning, #f5a623)' : 'rgba(255, 255, 255, 0.92)')};
`;

const ContributorsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1rem;
  padding-top: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.22);
  font-size: 0.78rem;
  opacity: 0.95;
`;

const AvatarStack = styled.div`
  display: flex;
  align-items: center;
`;

const StackAvatar = styled.div<{ $i: number }>`
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.28);
  border: 2px solid rgba(255, 255, 255, 0.5);
  margin-left: ${({ $i }) => ($i === 0 ? 0 : -8)}px;
  font-size: 0.62rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyHint = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: var(--textSecondary, var(--onSurfaceVariant));
`;

type ContentBlockType = 'text' | 'video' | 'image' | 'file' | 'quiz' | 'links';

type ContentBlock = {
  id: string;
  type: ContentBlockType;
};

const LESSON_CONTENT_VERSION = 1;

function parseStoredDescription(raw: string | null | undefined): { plainDescription: string; blocks: ContentBlock[] } {
  if (!raw?.trim()) return { plainDescription: '', blocks: [] };
  try {
    const parsed = JSON.parse(raw) as { version?: number; blocks?: unknown[]; plainDescription?: string };
    if (parsed && typeof parsed === 'object' && parsed.version === LESSON_CONTENT_VERSION && Array.isArray(parsed.blocks)) {
      return {
        plainDescription: typeof parsed.plainDescription === 'string' ? parsed.plainDescription : '',
        blocks: parsed.blocks
          .map((rawBlock) => {
            if (!rawBlock || typeof rawBlock !== 'object') return null;
            const block = rawBlock as Record<string, unknown>;
            if (typeof block.id !== 'string' || typeof block.type !== 'string') return null;
            const normalizedType: ContentBlockType =
              block.type === 'interactive'
                ? 'links'
                : block.type === 'links' || block.type === 'video' || block.type === 'image' || block.type === 'file' || block.type === 'quiz'
                  ? block.type
                  : 'text';
            return { id: block.id, type: normalizedType };
          })
          .filter((b): b is ContentBlock => Boolean(b)),
      };
    }
  } catch {
    /* legacy plain-text description */
  }
  return { plainDescription: raw, blocks: [] };
}

function classSubjectLabel(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length <= 1) return 'Class';
  return parts.slice(0, 2).join(' ');
}

function pillHue(index: number): 'a' | 'b' | 'c' {
  const m = index % 3;
  if (m === 0) return 'a';
  if (m === 1) return 'b';
  return 'c';
}

function initials(name: string): string {
  const p = name.split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return `${p[0]!.charAt(0)}${p[1]!.charAt(0)}`.toUpperCase();
}

function formatLessonDate(iso: string | null): {
  today: boolean;
  dayLabel: string;
  dayNum: string;
  month: string;
} {
  if (!iso) {
    return { today: false, dayLabel: 'TBD', dayNum: '—', month: '' };
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return { today: false, dayLabel: 'TBD', dayNum: '—', month: '' };
  }
  const today = new Date();
  const todayFlag = d.toDateString() === today.toDateString();
  return {
    today: todayFlag,
    dayLabel: todayFlag ? 'TODAY' : d.toLocaleDateString(undefined, { weekday: 'short' }).toUpperCase(),
    dayNum: String(d.getDate()),
    month: d.toLocaleDateString(undefined, { month: 'short' }).toUpperCase(),
  };
}

function scoreToLetter(score: number): string {
  if (score >= 90) return 'A';
  if (score >= 80) return 'B';
  if (score >= 70) return 'C';
  if (score >= 60) return 'D';
  return 'F';
}

function statusPriority(status: TeacherGradeRow['status']): number {
  if (status === 'graded' || status === 'returned') return 3;
  if (status === 'submitted') return 2;
  return 1;
}

function rowTimestamp(value?: string | null): number {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
}

function dedupeSubmissionRows(rows: TeacherGradeRow[]): TeacherGradeRow[] {
  const latestByStudentAndWork = new Map<string, TeacherGradeRow>();
  const passthrough: TeacherGradeRow[] = [];

  rows.forEach((row) => {
    const workType = row.work_type ?? 'assignment';
    const classId = row.class_id?.trim() ?? '';
    const className = row.class_name?.trim().toLowerCase() ?? '';
    const workTitle = row.work_title?.trim().toLowerCase() ?? '';
    const workKey = row.work_id?.trim()
      ? `${workType}:id:${row.work_id.trim()}`
      : classId && workTitle
        ? `${workType}:class:${classId}:title:${workTitle}`
        : className && workTitle
          ? `${workType}:class-name:${className}:title:${workTitle}`
          : '';
    const studentKey = row.student_id?.trim()
      ? `id:${row.student_id.trim()}`
      : row.student_name.trim()
        ? `name:${row.student_name.trim().toLowerCase()}`
        : '';
    if (!workKey || !studentKey) {
      passthrough.push(row);
      return;
    }
    const key = `${workKey}::${studentKey}`;
    const prev = latestByStudentAndWork.get(key);
    if (!prev) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    const prevIsMissing = prev.status === 'missing';
    const currIsMissing = row.status === 'missing';
    if (prevIsMissing !== currIsMissing) {
      latestByStudentAndWork.set(key, currIsMissing ? prev : row);
      return;
    }
    const prevTs = rowTimestamp(prev.submitted_at);
    const currTs = rowTimestamp(row.submitted_at);
    if (currTs > prevTs) {
      latestByStudentAndWork.set(key, row);
      return;
    }
    if (currTs === prevTs && statusPriority(row.status) > statusPriority(prev.status)) {
      latestByStudentAndWork.set(key, row);
    }
  });

  return Array.from(latestByStudentAndWork)
    .map(([, row]) => row)
    .concat(passthrough)
    .sort((a, b) => rowTimestamp(b.submitted_at) - rowTimestamp(a.submitted_at));
}

const ClassroomTeacherOverviewPage: React.FC = () => {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);

  const displayName =
    profile?.full_name?.trim() ||
    (typeof user?.user_metadata?.full_name === 'string' && user.user_metadata.full_name.trim()) ||
    user?.email?.split('@')[0] ||
    'Teacher';
  const [expandedLessonIds, setExpandedLessonIds] = useState<Set<string>>(new Set());

  const [overviewQuery, lessonsQuery, gradesQuery, activitySummaryQuery] = useQueries({
    queries: [
      { queryKey: ['classroom', 'teacher', 'overview'], queryFn: teacherService.getOverview },
      { queryKey: ['classroom', 'teacher', 'lessons'], queryFn: teacherService.getLessons },
      { queryKey: ['classroom', 'teacher', 'grades'], queryFn: teacherService.getGrades },
      { queryKey: ['classroom', 'teacher', 'activitySummary'], queryFn: teacherService.getActivitySummary },
    ],
  });

  const loading = overviewQuery.isLoading || lessonsQuery.isLoading || gradesQuery.isLoading || activitySummaryQuery.isLoading;
  const error = overviewQuery.isError || lessonsQuery.isError || gradesQuery.isError || activitySummaryQuery.isError;

  const classesData = overviewQuery.data;
  const lessonsData = lessonsQuery.data;
  const gradesData = gradesQuery.data;
  const activitySummary: TeacherActivitySummary | undefined = activitySummaryQuery.data;

  const classById = useMemo(() => {
    const m = new Map<string, string>();
    (classesData ?? []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [classesData]);

  const dedupedGrades = useMemo(() => dedupeSubmissionRows(gradesData ?? []), [gradesData]);

  const upcomingLessons = useMemo(() => {
    const list = [...(lessonsData ?? [])];
    list.sort((a, b) => {
      const da = a.lesson_date ? new Date(a.lesson_date).getTime() : Number.POSITIVE_INFINITY;
      const db = b.lesson_date ? new Date(b.lesson_date).getTime() : Number.POSITIVE_INFINITY;
      return da - db;
    });
    return list.slice(0, 5);
  }, [lessonsData]);

  const activityRows = useMemo(() => dedupedGrades.slice(0, 6), [dedupedGrades]);

  const totals = useMemo(() => {
    const rows = classesData ?? [];
    const totalStudents = rows.reduce((s, c) => s + c.activeStudents, 0);
    const assignmentsDue = rows.reduce((s, c) => s + c.upcomingAssignments, 0);
    const recentSubmissions = rows.reduce((s, c) => s + c.recentSubmissions, 0);
    return { totalStudents, assignmentsDue, recentSubmissions };
  }, [classesData]);

  const studentActivity = useMemo(() => {
    const lessonsCompleted = activitySummary?.lessonsCompleted ?? 0;
    const assignmentsCompleted = activitySummary?.assignmentsCompleted ?? 0;
    const missedAssignments = activitySummary?.assignmentsMissed ?? 0;
    const missedLessons = activitySummary?.lessonsMissed ?? 0;
    return {
      lessonsCompleted,
      assignmentsCompleted,
      missedAssignments,
      missedLessons,
    };
  }, [activitySummary]);

  const insights = useMemo(() => {
    const grades = dedupedGrades;
    const submitted = grades.filter((g) => g.status === 'submitted').length;
    const completed = grades.filter((g) => g.status === 'graded' || g.status === 'returned').length;
    const missed = grades.filter((g) => g.status === 'missing').length;
    const tracked = submitted + completed + missed;
    const completionPct = tracked > 0 ? Math.round((completed / tracked) * 100) : 0;
    const missedPct = tracked > 0 ? Math.round((missed / tracked) * 100) : 0;
    const scored = grades.filter((g) => g.score != null && Number.isFinite(g.score));
    const avgScore =
      scored.length > 0 ? scored.reduce((s, g) => s + (g.score as number), 0) / scored.length : null;
    const letter = avgScore != null ? scoreToLetter(avgScore) : '—';
    const avgBarPct = avgScore != null ? Math.min(100, Math.round(avgScore)) : 0;
    return { completionPct, missedPct, letter, avgBarPct };
  }, [dedupedGrades]);

  const topNames = useMemo(() => {
    const names = dedupedGrades.map((g) => g.student_name).filter(Boolean);
    const uniq = Array.from(new Set(names));
    return uniq.slice(0, 3);
  }, [dedupedGrades]);

  const extraContributors = useMemo(() => {
    const uniq = new Set(dedupedGrades.map((g) => g.student_name).filter(Boolean));
    return Math.max(0, uniq.size - topNames.length);
  }, [dedupedGrades, topNames.length]);

  const toggleExpanded = (lessonId: string) => {
    setExpandedLessonIds((prev) => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId);
      else next.add(lessonId);
      return next;
    });
  };

  if (loading) {
    return (
      <Page>
        <EmptyHint>Loading classroom overview…</EmptyHint>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <EmptyHint>Unable to load classroom overview.</EmptyHint>
      </Page>
    );
  }

  const classes = classesData ?? [];

  return (
    <Page>
      <Hero>
        <HeroText>
          <WelcomeTitle>Welcome back, {displayName}.</WelcomeTitle>
          <SummaryLine>
            You have <strong>{classes.length}</strong> active {classes.length === 1 ? 'class' : 'classes'} with{' '}
            <strong>{totals.totalStudents}</strong> students across your roster.
          </SummaryLine>
          <SummaryLine>
            <strong>{totals.assignmentsDue}</strong> assignments due this week ·{' '}
            <strong>{totals.recentSubmissions}</strong> recent submissions to review.
          </SummaryLine>
        </HeroText>
        <PrimaryButton to="/classroom-teacher/classes/new">
          <FaPlus size={14} aria-hidden />
          Create New Class
        </PrimaryButton>
      </Hero>

      <MainGrid>
        <MainCol>
          <Section>
            <SectionHead>
              <SectionTitle>
                <SectionTitleIcon>
                  <FaBook size={18} aria-hidden />
                </SectionTitleIcon>
                Active Classes
              </SectionTitle>
              <TextLink to="/classroom-teacher/classes">View all classes</TextLink>
            </SectionHead>
            {classes.length === 0 ? (
              <EmptyHint>No classes yet. When classes are added, they will appear here as cards.</EmptyHint>
            ) : (
              <ClassCardGrid>
                {classes.map((c: TeacherClassSummary, index: number) => {
                  const progressPct = Math.min(100, Math.round((c.recentSubmissions / Math.max(c.activeStudents, 1)) * 100));
                  return (
                    <CardLink key={c.id} to={`/classroom-teacher/classes/${encodeURIComponent(c.id)}`}>
                      <ClassCard>
                        <ClassCardVisual $variant={index % 2 === 1 ? 'b' : 'a'} $imageUrl={c.class_image_url}>
                          <SubjectPill $hue={pillHue(index)}>{classSubjectLabel(c.name)}</SubjectPill>
                        </ClassCardVisual>
                        <ClassCardBody>
                          <ClassTitle>{c.name}</ClassTitle>
                          <MetaRow>
                            <MetaItem>
                              <FaUserFriends size={13} aria-hidden />
                              {c.activeStudents} students
                            </MetaItem>
                            <MetaItem>
                              <FaClock size={12} aria-hidden />
                              {c.upcomingAssignments} due soon
                            </MetaItem>
                          </MetaRow>
                          <ProgressTrack>
                            <ProgressFill $pct={progressPct} $color="var(--primary)" />
                          </ProgressTrack>
                        </ClassCardBody>
                      </ClassCard>
                    </CardLink>
                  );
                })}
              </ClassCardGrid>
            )}
          </Section>

          <Section>
            <SectionHead>
              <SectionTitle>
                <SectionTitleIcon>
                  <FaCalendarAlt size={17} aria-hidden />
                </SectionTitleIcon>
                Upcoming Lessons
              </SectionTitle>
              <TextLink to="/classroom-teacher/lessons">View full calendar</TextLink>
            </SectionHead>
            {upcomingLessons.length === 0 ? (
              <EmptyHint>No upcoming lessons scheduled. Add dates on the Lessons page.</EmptyHint>
            ) : (
              <LessonList>
                {upcomingLessons.map((lesson: TeacherLesson) => {
                  const { dayLabel, dayNum, month } = formatLessonDate(lesson.lesson_date);
                  const className = classById.get(lesson.class_id) ?? 'Class';
                  const parsed = parseStoredDescription(lesson.description);
                  const isExpanded = expandedLessonIds.has(lesson.id);
                  return (
                    <LessonRow key={lesson.id}>
                      <LessonDateBlock>
                        <LessonDateLabel>{dayLabel}</LessonDateLabel>
                        <LessonDateDay>{dayNum}</LessonDateDay>
                        <LessonDateMonth>{month}</LessonDateMonth>
                      </LessonDateBlock>
                      <LessonCard>
                        <LessonTop>
                          <LessonName>{lesson.title}</LessonName>
                          <LessonTopRight>
                            <LessonClassTag>{className}</LessonClassTag>
                            <LessonToggle type="button" aria-expanded={isExpanded} onClick={() => toggleExpanded(lesson.id)}>
                              {isExpanded ? <FaChevronDown size={10} aria-hidden /> : <FaChevronRight size={10} aria-hidden />}
                              {isExpanded ? 'Hide details' : 'Show details'}
                            </LessonToggle>
                          </LessonTopRight>
                        </LessonTop>
                        {isExpanded ? (
                          <>
                            <LessonDesc>{parsed.plainDescription || 'Lesson details coming soon.'}</LessonDesc>
                            <PillRow>
                              <Pill>
                                <FaClock size={11} aria-hidden />
                                Scheduled
                              </Pill>
                              <Pill>
                                <FaPaperclip size={11} aria-hidden />
                                {parsed.blocks.length} {parsed.blocks.length === 1 ? 'Block' : 'Blocks'}
                              </Pill>
                              <Pill>Seq. {lesson.sequence}</Pill>
                            </PillRow>
                          </>
                        ) : null}
                      </LessonCard>
                    </LessonRow>
                  );
                })}
              </LessonList>
            )}
          </Section>
        </MainCol>

        <SideCol>
          <ActivitySection>
            <SectionHead style={{ marginBottom: '0.35rem' }}>
              <SectionTitle>
                <SectionTitleIcon>
                  <FaComments size={17} aria-hidden />
                </SectionTitleIcon>
                Student Activity
              </SectionTitle>
            </SectionHead>
            <ActivityMetricsGrid>
              <ActivityMetricCard>
                <ActivityMetricLabel>Lessons completed</ActivityMetricLabel>
                <ActivityMetricValue>{studentActivity.lessonsCompleted}</ActivityMetricValue>
              </ActivityMetricCard>
              <ActivityMetricCard>
                <ActivityMetricLabel>Assignments completed</ActivityMetricLabel>
                <ActivityMetricValue>{studentActivity.assignmentsCompleted}</ActivityMetricValue>
              </ActivityMetricCard>
              <ActivityMetricCard>
                <ActivityMetricLabel>Missed assignments</ActivityMetricLabel>
                <ActivityMetricValue>{studentActivity.missedAssignments}</ActivityMetricValue>
              </ActivityMetricCard>
              <ActivityMetricCard>
                <ActivityMetricLabel>Missed lessons</ActivityMetricLabel>
                <ActivityMetricValue>{studentActivity.missedLessons}</ActivityMetricValue>
              </ActivityMetricCard>
            </ActivityMetricsGrid>
            {activityRows.length === 0 ? (
              <EmptyHint>No recent submission activity yet.</EmptyHint>
            ) : (
              <ActivityList>
                {activityRows.map((row: TeacherGradeRow) => (
                  <ActivityItem key={row.id}>
                    <Avatar aria-hidden>{initials(row.student_name)}</Avatar>
                    <ActivityBody>
                      <strong>{row.student_name}</strong>{' '}
                      {row.status === 'graded' || row.status === 'returned'
                        ? `graded “${row.work_title}”`
                        : `submitted “${row.work_title}”`}
                      <ActivityMeta>Review queue · {row.status === 'submitted' ? 'Needs grading' : row.status}</ActivityMeta>
                      {row.status === 'submitted' ? <GradeLink to="/classroom-teacher/grades">Grade now</GradeLink> : null}
                      {row.score != null ? <ScoreBadge>Score: {row.score}%</ScoreBadge> : null}
                    </ActivityBody>
                  </ActivityItem>
                ))}
              </ActivityList>
            )}
            <ActivityFooter to="/classroom-teacher/grades">View all activity</ActivityFooter>
          </ActivitySection>

          <InsightsCard>
            <InsightsTitle>Class insights</InsightsTitle>
            <InsightRow>
              <InsightLabelRow>
                <span>Assignment completion</span>
                <span>{insights.completionPct}%</span>
              </InsightLabelRow>
              <InsightBarTrack>
                <InsightBarFill $pct={insights.completionPct} $tint="blue" />
              </InsightBarTrack>
            </InsightRow>
            <InsightRow>
              <InsightLabelRow>
                <span>Missed work rate</span>
                <span>{insights.missedPct}%</span>
              </InsightLabelRow>
              <InsightBarTrack>
                <InsightBarFill $pct={insights.missedPct} $tint="warm" />
              </InsightBarTrack>
            </InsightRow>
            <InsightRow>
              <InsightLabelRow>
                <span>Avg. score</span>
                <span>{insights.letter}</span>
              </InsightLabelRow>
              <InsightBarTrack>
                <InsightBarFill $pct={insights.avgBarPct} $tint="warm" />
              </InsightBarTrack>
            </InsightRow>
            <ContributorsRow>
              <AvatarStack>
                {topNames.map((n, i) => (
                  <StackAvatar key={`${n}-${i}`} $i={i} aria-hidden>
                    {initials(n)}
                  </StackAvatar>
                ))}
                {extraContributors > 0 ? (
                  <StackAvatar $i={topNames.length} aria-hidden>
                    +{extraContributors}
                  </StackAvatar>
                ) : null}
              </AvatarStack>
              <span>Top contributors</span>
            </ContributorsRow>
          </InsightsCard>
        </SideCol>
      </MainGrid>
    </Page>
  );
};

export default ClassroomTeacherOverviewPage;
